import { Suspense, lazy, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { useUIStore } from '@/store/uiStore';
import { useToast } from '@/components/ui/ToastProvider';
import { cn } from '@/lib/utils';
import { openActionUrl } from '@/lib/openActionUrl';
import NotificationDropdown from './NotificationDropdown';
import type { Notification } from '@/services/notificationsApi';

// T3108: okno obvestila (s skladom Radix Dialog) se naloži ob prvem odprtju — zvonec je
// v glavi vsake strani, zato bi ga sicer nosil vstopni graf.
const NotificationDetailModal = lazy(() => import('./NotificationDetailModal'));

function getBadgeLabel(count: number): string | null {
  if (count <= 0) return null;
  if (count >= 100) return '99+';
  return String(count);
}

// Exported for testing the cap logic in isolation
export function NotificationBadge({ count }: { count: number }) {
  const label = getBadgeLabel(count);
  if (!label) return null;
  return (
    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-caption font-bold leading-none border-2 border-white">
      {label}
    </span>
  );
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const { notificationPanelOpen, toggleNotificationPanel } = useUIStore();
  const { toast } = useToast();
  // T3124: toast samo za obvestilo visoke prioritete, ki je prišlo v živo med
  // sejo. Seznam, naložen ob prijavi, ostane v zvoncu (števec ga že kaže).
  const onLive = useCallback(
    (n: Notification) => {
      if (!n.is_read && n.priority === 'high') {
        toast({ type: 'info', title: n.title, message: n.body });
      }
    },
    [toast],
  );
  const { notifications, unreadCount, markRead, markAllRead, hasMore, loadMore } = useNotifications({ onLive });
  const navigate = useNavigate();

  // Lifted to the bell level so the detail modal survives the dropdown closing (T863).
  const [selected, setSelected] = useState<Notification | null>(null);

  return (
    <div className="relative">
      <button
        aria-label={t('notifications.page_title')}
        aria-expanded={notificationPanelOpen}
        onClick={() => toggleNotificationPanel()}
        className={cn(
          "p-2 rounded-lg transition-colors relative hover:bg-accent",
          unreadCount > 0 ? "text-foreground" : "text-muted-foreground hover:text-foreground"
        )}
      >
        <Bell className="h-5 w-5" />
        <NotificationBadge count={unreadCount} />
      </button>

      {notificationPanelOpen && (
        <>
          {/* Click-outside overlay */}
          <div
            className="fixed inset-0 z-[55]"
            onClick={() => toggleNotificationPanel(false)}
          />
          <div className="relative z-[60]">
            <NotificationDropdown
              notifications={notifications}
              unreadCount={unreadCount}
              hasMore={hasMore}
              onMarkRead={markRead}
              onMarkAllRead={markAllRead}
              onLoadMore={loadMore}
              onClose={() => toggleNotificationPanel(false)}
              onOpen={(n) => {
                setSelected(n);
                toggleNotificationPanel(false);
              }}
            />
          </div>
        </>
      )}

      {selected && (
        <Suspense fallback={null}>
          <NotificationDetailModal
            notification={selected}
            onClose={() => setSelected(null)}
            onNavigate={(url) => {
              setSelected(null);
              openActionUrl(url, navigate);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
